import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Box,
  Paper,
  Skeleton,
  Typography,
  Alert,
  Tooltip,
  Collapse,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import SouthEastRoundedIcon from '@mui/icons-material/SouthEastRounded'
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { alpha } from '@mui/material/styles'
import { Field, Form, Formik } from 'formik'
import { TextField } from 'formik-material-ui'
import { isEqual } from 'lodash'
import { memo, useContext, useEffect, useMemo, useState } from 'react'
import { metodoPrograma } from '../../../components/api'
import { setSuccess } from '../../../components/alerts'
import CurrencyField from '../../../components/CurrencyField/CurrencyField'
import { GlobalContext } from '../../../App'
import AutoComplete from '../../../components/AutoComplete/AutoComplete'

const initialValues = {
  acPaciente: null,
  acCliente: null,
  idPaciente: null,
  acAtendimento: null,
  idAtendimento: null,
  acEmpresa: null,
  idEmpresa: null,
  dsDescricao: '',
  vlNota: '',
}

const TotalPagoAtendimento = ({
  idAtendimento,
  open,
  resultado,
  setResultado,
  onUsarTotal,
  isSubmitting,
}) => {
  const [minimizado, setMinimizado] = useState(false)

  useEffect(() => {
    setMinimizado(false)
  }, [idAtendimento, open])

  useEffect(() => {
    let ativo = true
    setResultado(null)
    if (!open || !idAtendimento) return undefined

    metodoPrograma('totalPagoAtendimento', { idAtendimento }, 'CtrlNotaFiscal')
      .then((res) => {
        if (ativo) {
          setResultado({
            idAtendimento,
            total: res.status ? res.totalPago : null,
            proposta: res.totalProposta ?? 0,
            checkin: res.totalCheckin ?? 0,
            checkout: res.totalCheckout ?? 0,
            pontoVenda: res.totalPontoVenda ?? 0,
          })
        }
      })
      .catch(() => {
        if (ativo) setResultado({ idAtendimento, total: null })
      })

    return () => {
      ativo = false
    }
  }, [idAtendimento, open, setResultado])

  if (!idAtendimento) return null

  const atual = resultado?.idAtendimento === idAtendimento ? resultado : null
  const erro = atual && atual.total === null

  return (
    <Grid item xs={12}>
      <Paper
        elevation={0}
        role="status"
        aria-live="polite"
        aria-busy={!atual}
        sx={(theme) => ({
          p: minimizado ? 1.5 : { xs: 2, sm: 2.5 },
          borderRadius: 3,
          boxShadow: '0 3px 12px rgba(0, 0, 0, 0.10)',
          border: '1px solid',
          borderColor: alpha(
            erro ? theme.palette.error.main : theme.palette.success.main,
            0.25,
          ),
          background: `linear-gradient(120deg, ${alpha(
            erro ? theme.palette.error.main : theme.palette.success.main,
            0.09,
          )}, ${theme.palette.background.paper})`,
        })}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              flexShrink: 0,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.success.main, 0.12),
              color: 'success.main',
            })}
          >
            <AccountBalanceWalletOutlinedIcon />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: 'text.secondary' }}
            >
              Total pago pelo cliente
            </Typography>
            {!atual ? (
              <>
                <Skeleton width="65%" height={48} sx={{ maxWidth: 240 }} />
                <Typography variant="caption" color="text.secondary">
                  Consultando pagamentos…
                </Typography>
              </>
            ) : erro ? (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                Não foi possível consultar o total pago.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography
                  sx={{
                    mt: 0.5,
                    fontSize: minimizado ? 20 : { xs: 28, sm: 34 },
                    fontWeight: 700,
                    letterSpacing: '-0.5px',
                    fontVariantNumeric: 'tabular-nums',
                    color: 'success.dark',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {Number(atual.total).toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                </Typography>
                <Tooltip title="Usar total pago no valor da nota">
                  <span>
                    <IconButton
                      type="button"
                      size="small"
                      color="success"
                      aria-label="Usar total pago no valor da nota"
                      disabled={!atual || !!erro || isSubmitting}
                      onClick={() => onUsarTotal(Number(atual.total))}
                      sx={(theme) => ({
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.success.main, 0.08),
                        '&:hover': {
                          bgcolor: alpha(theme.palette.success.main, 0.16),
                        },
                      })}
                    >
                      <SouthEastRoundedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            )}
          </Box>
          <Tooltip title={minimizado ? 'Expandir resumo' : 'Minimizar resumo'}>
            <IconButton
              type="button"
              size="small"
              aria-label={minimizado ? 'Expandir resumo' : 'Minimizar resumo'}
              aria-expanded={!minimizado}
              aria-controls="resumo-pagamentos-atendimento"
              onClick={() => setMinimizado((anterior) => !anterior)}
            >
              {minimizado ? (
                <ExpandMoreRoundedIcon />
              ) : (
                <ExpandLessRoundedIcon />
              )}
            </IconButton>
          </Tooltip>
        </Box>
        <Collapse
          in={!minimizado && !!atual && !erro}
          id="resumo-pagamentos-atendimento"
        >
          {atual && !erro && (
            <Box
              sx={(theme) => ({
                mt: 2,
                pt: 2,
                borderTop: '1px solid',
                borderColor: alpha(theme.palette.success.main, 0.15),
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: `repeat(${atual.pontoVenda > 0 ? 4 : 3}, minmax(0, 1fr))`,
                },
                gap: 1.5,
              })}
            >
              {[
                ['Proposta', atual.proposta],
                ['Check-in', atual.checkin],
                ...(atual.pontoVenda > 0
                  ? [['Ponto de venda', atual.pontoVenda]]
                  : []),
                ['Checkout', atual.checkout],
              ].map(([label, valor]) => (
                <Box
                  key={label}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.5,
                      fontWeight: 600,
                      fontVariantNumeric: 'tabular-nums',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {Number(valor).toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    })}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Collapse>
      </Paper>
    </Grid>
  )
}

const AvisoValorNota = ({ resultado, idAtendimento, vlNota }) => {
  const atual = resultado?.idAtendimento === idAtendimento ? resultado : null
  const erro = atual && atual.total === null
  const valorNota = Number(vlNota)
  const valorDiferente =
    atual &&
    !erro &&
    vlNota !== '' &&
    vlNota !== null &&
    vlNota !== undefined &&
    Number.isFinite(valorNota) &&
    Math.round(valorNota * 100) !== Math.round(Number(atual.total) * 100)

  if (!valorDiferente) return null

  return (
    <Grid item xs={12}>
      <Alert severity="warning" sx={{ borderRadius: 2 }}>
        O valor da nota difere do total pago. A emissão pode continuar.
      </Alert>
    </Grid>
  )
}

const ModalNotaFiscal = ({ open, handleClose, setRefresh }) => {
  const [resultado, setResultado] = useState(null)
  const { localAtendimento } = useContext(GlobalContext)

  const emitir = async (values, { setSubmitting, resetForm }) => {
    const payload = {
      idPaciente: values.idCliente || null,
      idAtendimento: values.acAtendimento?.[0]?.[0] || null,
      idEmpresaNotaFiscal: values.acEmpresa?.[0]?.[0] || null,
      dsEmail: values.dsEmail || '',
      dsServico: values.dsDescricao || '',
      vlConta: values.vlNota,
    }

    const res = await metodoPrograma('emitirNotaFiscal', payload)

    setSubmitting(false)

    if (res.status) {
      setSuccess('Nota Fiscal emitida com sucesso!')
      setRefresh((prev) => prev + 1)
      resetForm()
      handleClose()
    }
  }

  const searchLocalAtendimento = useMemo(
    () => ({ idLocalAtendimento: localAtendimento?.[0] || null }),
    [localAtendimento],
  )

  return (
    <Formik initialValues={initialValues} enableReinitialize onSubmit={emitir}>
      {({
        isSubmitting,
        submitForm,
        values,
        errors,
        touched,
        setFieldValue,
        resetForm,
      }) => (
        <Form>
          <Dialog
            open={open}
            keepMounted
            fullWidth
            maxWidth="md"
            aria-describedby="modal-nota-fiscal"
          >
            <DialogTitle>Emitir Nota Fiscal</DialogTitle>
            <IconButton
              aria-label="close"
              onClick={() => {
                resetForm()
                handleClose()
              }}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>

            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <AutoComplete
                    InputLabelProps={{ shrink: true }}
                    label="Atendimento"
                    metodo="buscaAtendimentoPontoVenda"
                    searchParams={{
                      incluirFinalizados: true,
                      ordenarPorIdAtendimento: true,
                    }}
                    getOptionLabel={(item) =>
                      Array.isArray(item) && item.length > 0
                        ? `Proposta ${item?.[0]?.[0] ?? '-'} - ${item?.[1] ?? ''}`
                        : ''
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?.[0]?.[0] === value?.[0]?.[0]
                    }
                    value={values.acAtendimento}
                    onChange={(_, v) => {
                      setFieldValue('idAtendimento', v?.[0]?.[0])
                      setFieldValue('acAtendimento', v)
                      setFieldValue('acCliente', v?.[5])
                      setFieldValue('idCliente', v?.[5]?.[0]?.[0])
                      setFieldValue('dsEmail', v?.[5]?.[4])
                      setFieldValue('bloqueia', !!v)
                    }}
                    textFieldProps={{
                      error: touched.acAtendimento && !!errors.acAtendimento,
                      helperText: touched.acAtendimento && errors.acAtendimento,
                    }}
                  />
                </Grid>

                <TotalPagoAtendimento
                  idAtendimento={values.idAtendimento}
                  open={open}
                  resultado={resultado}
                  setResultado={setResultado}
                  onUsarTotal={(valor) => setFieldValue('vlNota', valor)}
                  isSubmitting={isSubmitting}
                />

                <Grid item xs={12}>
                  <AutoComplete
                    InputLabelProps={{ shrink: true }}
                    label="Cliente"
                    metodo="buscaPaciente"
                    searchParams={searchLocalAtendimento}
                    disabled={isSubmitting || values.bloqueia}
                    value={values.acCliente || null}
                    onChange={(_, v) => {
                      setFieldValue('idCliente', v?.[0]?.[0])
                      setFieldValue('acCliente', v)
                    }}
                    textFieldProps={{
                      error: touched.acCliente && !!errors.acCliente,
                      helperText: touched.acCliente && errors.acCliente,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    component={TextField}
                    InputLabelProps={{ shrink: true }}
                    name="dsEmail"
                    label="E-mail para envio da NFS-e"
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    size="small"
                    validate={(value) => {
                      if (!value) return undefined

                      const emailRegex =
                        /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i

                      if (!emailRegex.test(value)) {
                        return 'Informe um e-mail válido'
                      }

                      return undefined
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <AutoComplete
                    InputLabelProps={{ shrink: true }}
                    label="Empresa de Emissão"
                    metodo="buscaEmissorNotaFiscal"
                    value={values.acEmpresa}
                    size="small"
                    onChange={(_, v) => {
                      setFieldValue('idEmpresa', v?.[0]?.[0] || null)
                      setFieldValue('acEmpresa', v)
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <CurrencyField
                    InputLabelProps={{ shrink: true }}
                    label="Valor da Nota"
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    size="small"
                    value={values.vlNota}
                    onChange={(v) => setFieldValue('vlNota', v)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Field
                    component={TextField}
                    InputLabelProps={{ shrink: true }}
                    name="dsDescricao"
                    label="Descrição do Serviço"
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    size="small"
                    multiline
                    rows={3}
                  />
                </Grid>
                <AvisoValorNota
                  resultado={resultado}
                  idAtendimento={values.idAtendimento}
                  vlNota={values.vlNota}
                />
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button
                onClick={() => {
                  resetForm()
                  handleClose()
                }}
                variant="contained"
                color="primary"
                style={{ margin: '5px' }}
              >
                Cancelar
              </Button>
              <Button
                onClick={submitForm}
                disabled={isSubmitting}
                variant="contained"
                color="primary"
                style={{ margin: '5px' }}
              >
                Emitir
              </Button>
            </DialogActions>
          </Dialog>
        </Form>
      )}
    </Formik>
  )
}

export default memo(ModalNotaFiscal, isEqual)
